<script setup lang="ts">
import { X } from '@lucide/vue'
defineOptions({ inheritAttrs: false })
const props = withDefaults(
  defineProps<{
    open: boolean
    kind: 'drawer' | 'modal'
    title?: string
    eyebrow?: string
    description?: string
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
    mobileMode?: 'fullscreen' | 'sheet'
    height?: 'viewport' | 'content'
    bodyLayout?: 'default' | 'stack'
    closeOnBackdrop?: boolean
    closeOnEscape?: boolean
    pending?: boolean
    initialFocus?: string
    role?: 'dialog' | 'alertdialog'
    labelledby?: string
    describedby?: string
    bodyPadding?: 'none' | 'sm' | 'md' | 'lg'
    hideClose?: boolean
    closeLabel?: string
  }>(),
  {
    title: undefined,
    eyebrow: undefined,
    description: undefined,
    pending: undefined,
    initialFocus: undefined,
    labelledby: undefined,
    describedby: undefined,
    size: 'md',
    mobileMode: 'fullscreen',
    closeOnBackdrop: true,
    closeOnEscape: true,
    role: 'dialog',
    bodyPadding: 'lg',
    height: 'viewport',
    bodyLayout: 'stack',
    hideClose: false,
    closeLabel: 'Fermer',
  },
)
const emit = defineEmits<{ close: []; 'update:open': [value: boolean] }>()
const mounted = ref(false)
const panel = ref<HTMLElement>()
// Teleport is not an element root: forward owner scope IDs to the actual panel.
const instance = getCurrentInstance()
const ownerScopes: Record<string, string> = {}
let owner = instance?.parent
while (owner) {
  if (owner.vnode.scopeId) ownerScopes[owner.vnode.scopeId] = ''
  owner = owner.parent
}
defineExpose({
  focus: () => panel.value?.focus(),
  get element() {
    return panel.value
  },
})
const id = useId()
function close() {
  if (!props.pending) {
    emit('close')
    emit('update:open', false)
  }
}
const { layer, isTop } = useUiOverlay(
  toRef(props, 'open'),
  panel,
  () => {
    if (props.closeOnEscape) close()
  },
  () => props.initialFocus,
)
onMounted(() => {
  mounted.value = true
})
</script>
<template>
  <Teleport to="body">
    <Transition name="ui-overlay">
      <div
        v-if="mounted && open"
        class="ui-overlay"
        :class="[
          `ui-overlay--${kind}`,
          `ui-overlay--${mobileMode}`,
          `ui-overlay--height-${height}`,
          `ui-overlay--body-${bodyLayout}`,
        ]"
        :style="{ zIndex: `calc(var(--z-overlay) + ${layer} * 2)` }"
        @click.self="closeOnBackdrop && isTop() && close()"
      >
        <section
          ref="panel"
          v-bind="{ ...$attrs, ...ownerScopes }"
          class="ui-overlay-panel"
          :class="[`ui-overlay-panel--${kind}`, `ui-overlay-size--${size}`]"
          :data-ui-overlay-id="id"
          :role="role"
          aria-modal="true"
          :aria-labelledby="
            labelledby || (title || $slots['header-title'] ? `${id}-title` : undefined)
          "
          :aria-describedby="describedby || (description ? `${id}-description` : undefined)"
          :aria-busy="pending || undefined"
          tabindex="-1"
        >
          <header class="ui-overlay-header">
            <div
              v-if="$slots['header-leading']"
              class="ui-overlay-leading"
            >
              <slot name="header-leading" />
            </div>
            <div class="ui-overlay-heading">
              <small v-if="eyebrow">{{ eyebrow }}</small>
              <h2
                v-if="title || $slots['header-title']"
                :id="`${id}-title`"
              >
                <slot name="header-title">{{ title }}</slot>
              </h2>
              <p
                v-if="description"
                :id="`${id}-description`"
              >
                {{ description }}
              </p>
              <slot name="header-meta" />
              <!-- Compatibility slot supplies content only; closing remains owned by the shell. -->
              <slot name="header" />
            </div>
            <div class="ui-overlay-actions">
              <slot name="header-actions" />
              <UiIconButton
                v-if="!hideClose || closeOnBackdrop || closeOnEscape"
                :aria-label="closeLabel"
                :disabled="pending"
                @click="close"
                ><X
                  :size="20"
                  aria-hidden="true"
              /></UiIconButton>
            </div>
          </header>
          <div
            class="ui-overlay-body"
            :class="`ui-overlay-body--${bodyPadding}`"
          >
            <slot />
          </div>
          <footer
            v-if="$slots.footer"
            class="ui-overlay-footer"
          >
            <slot name="footer" />
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
